import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Typography } from '@mui/material';
import { DatePicker } from './DatePicker';
import { FieldWrapper } from '../FieldWrapper';
import { ICalendar, ICalendarTime, IError } from '../../Iconography';
import { Dropdown, DropdownClearButton } from '../../Dropdown';
import { DropdownField } from '../../Dropdown/Dropdown.components';
import { HelixPalette, HelixStyles } from '../../../helix';
/**
 * Date field component.
 */
export const DateField = (props) => {
    const { clearable = true, dataId, dateFormat = 'dd MMM yyyy', disabled = false, error, events, height = 40, helpText = '', id, includeTime = false, label, onChange, placeholder, readOnly = false, required = false, startActive = false, style, timeFormat = 'HH:mm', value = null, width = '100%', } = props;
    // Refs.
    const dropdownRef = useRef(null);
    // State.
    const [open, setOpen] = useState(false);
    const [rightAdornment, setRightAdornment] = useState(null);
    /**
     * Calls onChange when the Date value changes.
     * @param value The new Date value.
     */
    const handleOnChange = (value) => {
        onChange((value === null || value === void 0 ? void 0 : value.date) || null);
        // Close the picker if there is not a time picker.
        if (!includeTime)
            setOpen(false);
    };
    /**
     * Handler called when the dropdown button is clicked.
     */
    const handleOnClick = () => {
        // Only open the date picker if the fiels is not read only.
        if (!readOnly)
            setOpen(true);
    };
    // Sets the field to active.
    useEffect(() => {
        if (dropdownRef.current && startActive)
            dropdownRef.current.click();
    }, [dropdownRef.current]);
    // Decide on the right adornment to render.
    useEffect(() => {
        let adornment = null;
        // Display error icon.
        if (error)
            adornment = _jsx(IError, { dataId: `${dataId}-error-icon`, color: HelixPalette.danger });
        // Display calendar time icon.
        else if (includeTime)
            adornment = _jsx(ICalendarTime, { dataId: `${dataId}-datetime-icon`, color: disabled ? HelixPalette.neutral50 : HelixPalette.neutral90 });
        // Display calendar icon.
        else
            adornment = _jsx(ICalendar, { dataId: `${dataId}-date-icon`, color: disabled ? HelixPalette.neutral50 : HelixPalette.neutral90 });
        setRightAdornment(adornment);
    }, [error, includeTime, disabled]);
    return (_jsxs(_Fragment, { children: [_jsx(FieldWrapper, Object.assign({ dataId: dataId, error: Boolean(error), helpText: error || helpText, id: id, label: label, required: required, width: width, style: style }, { children: _jsxs(DropdownField, Object.assign({ ref: dropdownRef, "aria-label": label || id, "data-error": Boolean(error), "data-id": `${dataId}-dropdown-field`, role: "dialog", type: "button", disabled: disabled, id: id, onClick: handleOnClick, placeholder: placeholder, sx: { height }, className: open ? 'active' : 'inactive' }, { children: [_jsx(Typography, Object.assign({ "data-id": `${dataId}-dropdown-value` }, HelixStyles.style_07, HelixStyles.truncate, { color: disabled ? HelixPalette.neutral50 : value ? HelixPalette.neutral90 : HelixPalette.neutral30, whiteSpace: "nowrap" }, { children: value ? format(value, includeTime ? `${dateFormat} ${timeFormat}` : dateFormat) : placeholder || label })), rightAdornment] })) })), _jsxs(Dropdown, Object.assign({ dataId: `${dataId}-dropdown`, anchorEl: dropdownRef.current, id: id, height: 406, onClose: () => setOpen(false), open: open, style: { minHeight: 365, minWidth: 309 } }, { children: [_jsx(DatePicker, { dataId: `${dataId}-picker`, dateFormat: dateFormat, defaultValue: value !== null ? { date: value } : undefined, enableTimePicker: includeTime, onChange: handleOnChange, events: events }), clearable && value && _jsx(DropdownClearButton, { dataId: dataId, onClear: () => onChange(null) })] }))] }));
};
export default DateField;
